import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Upload, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/i18n";
import api, { apiError } from "@/api";


export default function PostJob() {
  const { t, catName, cityName } = useI18n();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const invite = sp.get("invite") || "";
  const inviteName = sp.get("name") || "";
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: invite ? (sp.get("cat") || "") : "", city: sp.get("city") || "", budget: "", images: [] });

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/cities").then((r) => setCities(r.data));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      setForm((p) => ({ ...p, images: [...p.images, ...urls] }));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/jobs", { ...form, budget: form.budget ? Number(form.budget) : null, ...(invite ? { invited_craftsman_id: invite } : {}) });
      toast.success(t("postJob.success"));
      nav("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-14">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2D231F] mb-8">{t("postJob.title")}</h1>
        <div data-testid="postjob-free-note" className="rounded-xl bg-[#EEF3E6] border border-[#5C6646] text-[#2D231F] text-sm font-medium px-4 py-3 mb-6 flex gap-2 items-center"><CheckCircle2 className="w-5 h-5 text-[#5C6646] shrink-0" />{t("postJob.freeInfo")}</div>
        {invite && <div data-testid="postjob-invite-note" className="rounded-xl bg-[#FFF4E0] border border-[#E69625] text-[#2D231F] text-sm font-medium px-4 py-3 mb-6">{t("again.banner").replace("{name}", inviteName || "—")}</div>}
        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#EBDDD3] p-8 space-y-5">
          <div className="space-y-2">
            <Label>{t("postJob.jobTitle")}</Label>
            <Input data-testid="job-title" required value={form.title} onChange={set("title")} className="rounded-lg" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("postJob.category")}</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="job-category" className="rounded-lg"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => <SelectItem key={c} value={c}>{catName(c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("postJob.city")}</Label>
              <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
                <SelectTrigger data-testid="job-city" className="rounded-lg"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {cities.map((c) => <SelectItem key={c} value={c}>{cityName(c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("postJob.budget")}</Label>
            <Input data-testid="job-budget" type="number" value={form.budget} onChange={set("budget")} className="rounded-lg" />
          </div>
          <div className="space-y-2">
            <Label>{t("postJob.desc")}</Label>
            <Textarea data-testid="job-description" required rows={5} value={form.description} onChange={set("description")} className="rounded-lg" />
            <p data-testid="guard-hint" className="text-xs text-[#8C7A70]">{t("guard.hint")}</p>
          </div>
          <div className="space-y-2">
            <Label>{t("postJob.photos")}</Label>
            <p className="text-xs text-[#8C7A70]">{t("postJob.photosHint")}</p>
            <div className="grid grid-cols-3 gap-2">
              {form.images.map((url, i) => (
                <div key={i} className="relative group">
                  <img src={url} alt="" className="w-full h-20 object-cover rounded-lg" />
                  <button type="button" data-testid={`remove-job-image-${i}`}
                    onClick={() => setForm((p) => ({ ...p, images: p.images.filter((_, x) => x !== i) }))}
                    className="absolute -top-1 -right-1 bg-destructive text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <label className="cursor-pointer h-20 rounded-lg border-2 border-dashed border-[#DCC2B3] flex items-center justify-center text-[#A89A90] hover:border-[#C65338]" data-testid="job-image-upload">
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadImages} />
                <Upload className="w-5 h-5" />
              </label>
            </div>
            {uploading && <p className="text-xs text-[#C65338]">{t("portfolio.uploading")}</p>}
          </div>
          <Button data-testid="job-submit" type="submit" disabled={!form.category || uploading} className="w-full rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white h-11 font-semibold">{t("postJob.submit")}</Button>
        </form>
      </motion.div>
    </div>
  );
}
